import type { Category } from '../../components/agent/scheduleDay'

/**
 * A record of every change a supervisor has made to a planning board cell
 * this session, held the same way planningBoardDraftStore.ts holds the
 * overrides themselves: sessionStorage keyed by whoever is signed in,
 * further keyed by the team's id. Unlike the draft, this is
 * append-only: applying a status or code again to the same cell adds a new
 * entry rather than replacing the old one, so docs/design/journey-map.md's
 * "open the history of a cell" has something to show. There is no backend
 * audit trail yet (see docs/product/delivery-order.md), so this only ever
 * reaches the supervisor who made the change, in the browser they made it
 * in.
 */
export type BoardHistoryEntry = {
  key: string
  category: Category
  label: string
  detail?: string
  changedBy: string
  changedAt: string
}

const STORAGE_KEY = 'muster.supervisor.planningBoardHistory'

function storageKey(userId: string, teamId: string): string {
  return `${STORAGE_KEY}:${userId}:${teamId}`
}

export function getBoardHistory(userId: string, teamId: string): BoardHistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(storageKey(userId, teamId))
    return raw ? (JSON.parse(raw) as BoardHistoryEntry[]) : []
  } catch {
    return []
  }
}

// Returns whether the write actually landed, the same as
// planningBoardDraftStore.ts's saveBoardDraft: storage can be unavailable,
// and a history entry that silently failed to record is worse than one
// that is known to be missing.
export function appendBoardHistory(userId: string, teamId: string, entries: BoardHistoryEntry[]): boolean {
  if (entries.length === 0) return true
  try {
    const current = getBoardHistory(userId, teamId)
    sessionStorage.setItem(storageKey(userId, teamId), JSON.stringify([...current, ...entries]))
    return true
  } catch {
    return false
  }
}

// Newest first, since a supervisor opening a cell's history wants to see
// the most recent change without scrolling for it.
export function historyForCell(entries: BoardHistoryEntry[], key: string): BoardHistoryEntry[] {
  return entries.filter((entry) => entry.key === key).sort((a, b) => b.changedAt.localeCompare(a.changedAt))
}
